/**
 * Authentication Service for Stenope Academy
 * 
 * Handles API calls for authentication operations.
 */

import axios from 'axios';

import { getApiUrl } from '../utils/config';
import { ACADEMY_AUTH_TOKEN_KEY, ACADEMY_AUTH_USER_KEY } from '../utils/authStorage';

const API_URL = getApiUrl();

class AuthService {
    constructor() {
        this.token = null;
        this.setupInterceptors();
    }

    setupInterceptors() {
        // Request interceptor to add auth token
        axios.interceptors.request.use(
            (config) => {
                const token = this.token || localStorage.getItem(ACADEMY_AUTH_TOKEN_KEY);
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor to handle auth errors
        axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    const code = error.response.data?.code;
                    this.clearAuthToken();
                    localStorage.removeItem(ACADEMY_AUTH_TOKEN_KEY);
                    localStorage.removeItem(ACADEMY_AUTH_USER_KEY);
                    
                    // Clear all quiz attempt data
                    Object.keys(localStorage).forEach(key => {
                        if (key.startsWith('quiz_')) {
                            localStorage.removeItem(key);
                        }
                    });
                    localStorage.removeItem('progress');

                    if (!window.location.pathname.includes('/login')) {
                        if (code === 'SESSION_OVERWRITTEN') {
                            window.location.href = '/login?reason=session_expired';
                        } else {
                            window.location.href = '/login';
                        }
                    }
                }
                return Promise.reject(error);
            }
        );
    }

    setAuthToken(token) {
        this.token = token;
    }

    clearAuthToken() {
        this.token = null;
    }

    async login(username, password, force = false) {
        const response = await axios.post(`${API_URL}/academy/login`, {
            username,
            password,
            force,
        });
        return response.data;
    }

    async verifyToken(token) {
        try {
            const originalToken = this.token;
            this.token = token;

            const response = await axios.get(`${API_URL}/academy/verify`);

            this.token = originalToken;
            return response.data;
        } catch (error) {
            if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                return false;
            }
            throw error;
        }
    }

    async updatePathway(pathway) {
        const response = await axios.post(`${API_URL}/academy/profile/pathway`, {
            pathway
        });
        return response.data;
    }

    async logout() {
        try {
            const response = await axios.post(`${API_URL}/academy/logout`);
            this.clearAuthToken();
            localStorage.removeItem(ACADEMY_AUTH_TOKEN_KEY);
            localStorage.removeItem(ACADEMY_AUTH_USER_KEY);
            return response.data;
        } catch (error) {
            this.clearAuthToken();
            localStorage.removeItem(ACADEMY_AUTH_TOKEN_KEY);
            localStorage.removeItem(ACADEMY_AUTH_USER_KEY);
            throw error;
        }
    }
}

const authService = new AuthService();
export default authService;
